// ===========================================
// Invoice Detail Page
// ===========================================
// Detailed view of a single invoice with payment history

import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import SendIcon from '@mui/icons-material/Send';
import PaymentIcon from '@mui/icons-material/Payment';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import UndoIcon from '@mui/icons-material/Undo';
import DiscountIcon from '@mui/icons-material/Discount';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditNoteIcon from '@mui/icons-material/EditNote';
import EditInvoiceDialog from '../../components/invoices/EditInvoiceDialog';
import InvoiceAmendmentHistory from '../../components/invoices/InvoiceAmendmentHistory';
import InvoiceReminderHistory from '../../components/invoices/InvoiceReminderHistory';
import InvoiceLateFeesSection from '../../components/invoices/InvoiceLateFeesSection';
import RemindersHoldControl from '../../components/invoices/RemindersHoldControl';
import {
  AmendBannerContext,
  amendUnavailableReason,
  buildAmendBannerContext,
  canAmendInvoice,
  checkoutLockMessage,
  isCheckoutLive,
} from '../../utils/invoiceAmend';
import { useLesson } from '../../hooks/useLessons';
import PageHeader from '../../components/common/PageHeader';
import { formatUTCDate } from '../../utils/dateUtils';
import { displayLineDescription } from '../../utils/invoiceLineDescription';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import VoidInvoiceDialog from '../../components/invoices/VoidInvoiceDialog';
import InvoiceNotBilledNotices from '../../components/invoices/InvoiceNotBilledNotices';

import RecordPaymentDialog from '../../components/invoices/RecordPaymentDialog';
import VoidPaymentDialog from '../../components/invoices/VoidPaymentDialog';
import NeedsCheckingDialog from '../../components/invoices/NeedsCheckingDialog';
import { useFamilyCredit } from '../../hooks/useFamilyCredit';
import { useSchoolTimezone } from '../../contexts/TenantContext';
import { usePermissions } from '../../hooks/usePermissions';
import type { Invoice, Payment } from '../../api/invoices.api';
import AddProductToInvoiceItemDialog from '../../components/invoices/AddProductToInvoiceItemDialog';
import InvoiceDiscountApprovalModal from '../../components/admin/invoices/InvoiceDiscountApprovalModal';
import DiscountAuditTrail from '../../components/admin/discounts/DiscountAuditTrail';
import {
  useAdminInvoice,
  useSendInvoice,
  useCancelInvoice,
  useDeleteInvoice,
} from '../../hooks/useInvoices';
import {
  useInvoiceDiscountDetails,
  useRemoveDiscount,
  useRecalculateDiscounts,
} from '../../hooks/useDiscounts';
import { formatCurrencyFromDollars, paymentDetailText } from '../../utils/currency';
import { appliedCoverageSuffix } from '../../utils/discountCoverage';
import {
  INVOICE_STATUS_COLORS as STATUS_COLORS,
  PAYMENT_METHOD_LABELS,
  invoiceStatusLabel,
  invoiceBalance,
  formatInvoiceDate as formatDate,
  formatRecordedAt,
  formatPaymentDay,
  livePayments,
  paymentToCredit,
  paymentNeedsChecking,
  PAYMENT_CHECK_REASON_LABELS,
} from '../../utils/invoiceDisplay';

/**
 * The day an invoice was voided. `cancelledAt` is an INSTANT (not a @db.Date), so it renders
 * in the viewer's local time, dd/mm/yyyy, like `sentAt`.
 */
function formatVoidedDay(iso: string): string {
  return new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(
    new Date(iso)
  );
}

// ===========================================
// HELPER FUNCTIONS
// ===========================================

// Invoice money is stored in dollars (invoice `total`, unit prices).
const formatCurrency = formatCurrencyFromDollars;

// ===========================================
// COMPONENT
// ===========================================

/** Where an "Amend" link from the class / enrolment prompt came from. */
interface AmendDeepLink {
  lessonId: string | null;
  studentId: string | null;
}

/**
 * Loads the class an amend deep link named and reports the banner subject, or null when it
 * cannot be loaded (the dialog then opens WITHOUT a banner rather than blocking). A component
 * of its own so the lesson query only exists when a deep link asked for it.
 */
function AmendContextLoader({
  link,
  onContext,
}: {
  link: AmendDeepLink;
  onContext: (context: AmendBannerContext | null) => void;
}) {
  const { data: lesson } = useLesson(link.lessonId ?? '');
  useEffect(() => {
    onContext(lesson ? buildAmendBannerContext(lesson, link.studentId) : null);
  }, [lesson, link.studentId, onContext]);
  return null;
}

export default function InvoiceDetailPage() {
  const { id, schoolSlug } = useParams<{ id: string; schoolSlug: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Dialog states
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  // The payment stays set while its dialog fades out; the open flag is what
  // closes it, so the title and message do not flicker mid-transition.
  const [paymentToEdit, setPaymentToEdit] = useState<Payment | null>(null);
  const [editPaymentOpen, setEditPaymentOpen] = useState(false);
  // #642 D4: payments are voided (never deleted); an account-credit row is "taken off".
  const [paymentToVoid, setPaymentToVoid] = useState<Payment | null>(null);
  const [voidPaymentOpen, setVoidPaymentOpen] = useState(false);
  // #642 Phase 2 Request 7: the owner's "Needs checking" flag (set or resolve).
  const [paymentToCheck, setPaymentToCheck] = useState<Payment | null>(null);
  const [checkMode, setCheckMode] = useState<'set' | 'resolve'>('set');
  const [checkOpen, setCheckOpen] = useState(false);
  const openCheck = (payment: Payment, mode: 'set' | 'resolve') => {
    setPaymentToCheck(payment);
    setCheckMode(mode);
    setCheckOpen(true);
  };
  const timeZone = useSchoolTimezone();
  const { can, isOwner } = usePermissions();
  // #372: waive, hold and the exemption need BOTH (the server's pair).
  const canChangeLateFees = can('operations_invoices', 'write') && can('operations_pricing', 'delete');
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [removeDiscountDialogOpen, setRemoveDiscountDialogOpen] = useState(false);
  const [discountToRemove, setDiscountToRemove] = useState<string | null>(null);
  const [removalReason, setRemovalReason] = useState('');
  const [auditTrailExpanded, setAuditTrailExpanded] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [amendOpen, setAmendOpen] = useState(false);
  // A SNAPSHOT taken on open: passing the live query result would reset the
  // admin's half-typed amendment on every background refetch (window focus).
  const [amendTarget, setAmendTarget] = useState<Invoice | null>(null);
  // Amend deep link (`?amend=1&lessonId=…[&studentId=…]`, from "An invoice may need amending"):
  // `pendingLink` waits for the invoice to LOAD; `amendLink`/`amendBanner` live while the dialog is open.
  const [pendingLink, setPendingLink] = useState<AmendDeepLink | null>(null);
  const [amendLink, setAmendLink] = useState<AmendDeepLink | null>(null);
  const [amendBanner, setAmendBanner] = useState<AmendBannerContext | null>(null);
  const [amendNotice, setAmendNotice] = useState<string | null>(null);
  const [amendHistoryExpanded, setAmendHistoryExpanded] = useState(true);
  const [reminderHistoryExpanded, setReminderHistoryExpanded] = useState(true);

  // Queries and mutations
  const { data: invoice, isLoading, isFetching, error, refetch } = useAdminInvoice(id || '');
  const { data: discountDetails } = useInvoiceDiscountDetails(id || '');
  const sendMutation = useSendInvoice();
  const cancelMutation = useCancelInvoice();
  const deleteMutation = useDeleteInvoice();
  const removeDiscountMutation = useRemoveDiscount(id || '');
  const recalculateMutation = useRecalculateDiscounts(id || '');

  // A family's live Stripe checkout locks amending until it expires. Refetch at
  // the recorded expiry so the button re-enables without a reload (the server
  // gate compares to now as well, so this is display only).
  // ⚠️ The CLOCK re-enables the button, not the refetch: a refetch that returns
  // the same data keeps the same object (React Query structural sharing), so
  // nothing would re-render and the tooltip would name a time already past.
  const [now, setNow] = useState(() => Date.now());
  const checkoutExpiresAt = invoice?.checkoutExpiresAt ?? null;
  useEffect(() => {
    if (!checkoutExpiresAt || !id) return;
    const wait = new Date(checkoutExpiresAt).getTime() - Date.now();
    if (wait <= 0) return;
    const timer = setTimeout(() => {
      setNow(Date.now());
      void refetch();
    }, Math.min(wait + 1000, 2 ** 31 - 1));
    return () => clearTimeout(timer);
  }, [checkoutExpiresAt, id, refetch]);

  // Deep link, step 1: read the params FIRST, then clear them so a refresh (or Back) cannot
  // reopen the dialog.
  useEffect(() => {
    if (searchParams.get('amend') !== '1') return;
    setPendingLink({
      lessonId: searchParams.get('lessonId'),
      studentId: searchParams.get('studentId'),
    });
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  // Step 2: once the invoice has LOADED (not before — no flash, and never on stale cached
  // data mid-refetch), open the amend dialog if it can be amended right now, else say why.
  useEffect(() => {
    if (!pendingLink || !invoice || invoice.id !== id || isFetching) return;
    const canWrite = can('operations_invoices', 'write');
    const live = isCheckoutLive(invoice, new Date());
    if (invoice.status === 'DRAFT' && canWrite) {
      // A draft is EDITED, not amended (D-b): the same Edit dialog as the page's own
      // button, with the same banner saying what changed.
      setAmendNotice(null);
      setAmendLink(pendingLink);
      setEditOpen(true);
    } else if (canAmendInvoice(invoice) && canWrite && !live) {
      setAmendNotice(null);
      setAmendTarget(invoice);
      setAmendLink(pendingLink);
      setAmendOpen(true);
    } else {
      setAmendNotice(amendUnavailableReason(invoice, timeZone, canWrite, live));
    }
    setPendingLink(null);
  }, [pendingLink, invoice, id, isFetching, can, timeZone]);

  // Handlers
  const handleSend = async () => {
    if (!invoice) return;
    await sendMutation.mutateAsync(invoice.id);
    setSendDialogOpen(false);
  };

  const handleVoid = async (data: { reason: string; notifyFamily: boolean }) => {
    if (!invoice) return;
    try {
      await cancelMutation.mutateAsync({ id: invoice.id, ...data });
      setCancelDialogOpen(false);
    } catch {
      // onError already shows the server's reason (payments, live checkout); keep the dialog open
    }
  };

  const handleDelete = async () => {
    if (!invoice) return;
    await deleteMutation.mutateAsync(invoice.id);
    setDeleteDialogOpen(false);
    navigate(`/${schoolSlug}/admin/invoices`);
  };

  // Manual payments can be corrected (reference / notes) or voided; Stripe ones mirror what
  // Stripe charged; an account-credit row is taken off instead (#642 D15). Never a voided row.
  const isAmendable = (payment: Payment) =>
    !payment.voidedAt &&
    !payment.stripePaymentId &&
    payment.method !== 'STRIPE' &&
    payment.method !== 'ACCOUNT_CREDIT' &&
    invoice?.status !== 'CANCELLED' &&
    invoice?.status !== 'REFUNDED';
  const canTakeOff = (payment: Payment) =>
    !payment.voidedAt && payment.method === 'ACCOUNT_CREDIT' && invoice?.status !== 'CANCELLED';
  const canEditPayments = can('operations_invoices', 'write');
  const canVoidPayments = can('operations_invoices', 'delete');
  // #642: the family's credit, for the Payment Summary (Phase 2 Request 6) and, on a DRAFT, what
  // will come off at send (D2). Read only with the route's own permission (#272).
  const { data: familyCredit } = useFamilyCredit(invoice?.familyId, can('operations_invoices', 'read'));

  const handleRemoveDiscount = async () => {
    if (!discountToRemove || !removalReason.trim()) return;
    await removeDiscountMutation.mutateAsync({
      discountId: discountToRemove,
      data: { reason: removalReason },
    });
    setRemoveDiscountDialogOpen(false);
    setDiscountToRemove(null);
    setRemovalReason('');
  };

  const handleRecalculate = async () => {
    await recalculateMutation.mutateAsync();
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !invoice) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Invoice not found or failed to load.
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          component={Link}
          to={`/${schoolSlug}/admin/invoices`}
          sx={{ mt: 2 }}
        >
          Back to Invoices
        </Button>
      </Box>
    );
  }

  const balance = invoiceBalance(invoice);
  const canRecordPayment =
    invoice.status === 'SENT' ||
    invoice.status === 'PARTIALLY_PAID' ||
    invoice.status === 'OVERDUE';
  // Amend (2026-09-26): a sent invoice with no payment yet. The server is the real gate.
  const canAmend = canAmendInvoice(invoice) && can('operations_invoices', 'write');
  const checkoutLive = isCheckoutLive(invoice, new Date(now));
  // Void: an issued invoice with no payment ROWS (PARTIALLY_PAID/REFUNDED/PAID are refused
  // by the server, so the button is not offered for them). The server is the real gate.
  const canVoid =
    (invoice.status === 'SENT' || invoice.status === 'OVERDUE') &&
    livePayments(invoice).length === 0 &&
    can('operations_invoices', 'write');
  const heldCredit = familyCredit ? parseFloat(familyCredit.available) : 0;

  return (
    <Box sx={{ p: 3 }}>
      {amendNotice && (
        <Alert severity="warning" onClose={() => setAmendNotice(null)} sx={{ mb: 2 }} data-testid="amend-link-notice">
          {amendNotice}
        </Alert>
      )}
      <PageHeader
        title={`Invoice ${invoice.invoiceNumber}`}
        subtitle={`${invoice.Family.name} - ${invoice.Term?.name || 'No Term'}`}
        action={
          // Up to five buttons: wrap onto more rows on a phone, labels never wrap (2026-09-30).
          // Desktop (lg+): ONE row — the buttons keep their width and the title gives way
          // (Andrew, 02/10/2026). Narrower screens still wrap.
          <Box
            sx={{
              display: 'flex',
              flexWrap: { xs: 'wrap', lg: 'nowrap' },
              flexShrink: { lg: 0 },
              gap: 1,
              '& .MuiButton-root': { whiteSpace: 'nowrap' },
              // Phone: each row fills evenly. Amend/Void sit inside a Tooltip <span>, so the
              // span is the flex item and its button stretches to fill it.
              '& > *': { flexGrow: { xs: 1, sm: 0 } },
              '& > span > .MuiButton-root': { width: '100%' },
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              component={Link}
              to={`/${schoolSlug}/admin/invoices`}
            >
              Back
            </Button>
            {invoice.status === 'DRAFT' && (
              <>
                <Button startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
                  Edit Invoice
                </Button>
                <Button
                  startIcon={<AddShoppingCartIcon />}
                  onClick={() => setAddProductDialogOpen(true)}
                >
                  Add Product
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => setSendDialogOpen(true)}
                >
                  Send Invoice
                </Button>
                <Button
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  Delete
                </Button>
              </>
            )}
            {canAmend && (
              <Tooltip title={checkoutLive ? checkoutLockMessage(invoice, timeZone) : ''}>
                {/* span: a disabled button emits no events, so the tooltip needs a
                    wrapper — focusable while disabled so keyboard users get the reason */}
                <span tabIndex={checkoutLive ? 0 : undefined}>
                  <Button
                    startIcon={<EditNoteIcon />}
                    onClick={() => {
                      setAmendTarget(invoice);
                      setAmendOpen(true);
                    }}
                    disabled={checkoutLive}
                  >
                    Amend Invoice
                  </Button>
                </span>
              </Tooltip>
            )}
            {canRecordPayment && (
              <Button
                variant="contained"
                color="success"
                startIcon={<PaymentIcon />}
                onClick={() => setRecordPaymentOpen(true)}
              >
                Record Payment
              </Button>
            )}
            {canVoid && (
              <Tooltip title={checkoutLive ? checkoutLockMessage(invoice, timeZone, 'Voiding') : ''}>
                <span tabIndex={checkoutLive ? 0 : undefined}>
                  <Button
                    color="warning"
                    startIcon={<CancelIcon />}
                    onClick={() => setCancelDialogOpen(true)}
                    disabled={checkoutLive}
                  >
                    Void Invoice
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        }
      />

      {invoice.status === 'CANCELLED' && (
        <Alert severity="info" sx={{ mb: 3 }} data-testid="void-banner">
          <strong>Voided</strong>
          {invoice.cancelledAt ? ` ${formatVoidedDay(invoice.cancelledAt)}` : ''}
          {invoice.cancelledByName ? ` by ${invoice.cancelledByName}` : ''}.{' '}
          {invoice.cancellationReason
            ? `Reason: ${invoice.cancellationReason}`
            : 'No reason recorded. This invoice was voided before reasons were kept.'}
        </Alert>
      )}
      {invoice.status === 'CANCELLED' && parseFloat(invoice.amountPaid) > 0 && (
        // A void is final, so a payment that still reached it (a late online payment)
        // keeps it VOID; the money is real and needs refunding (/qa L4).
        <Alert severity="warning" sx={{ mb: 3 }} data-testid="void-payment-warning">
          {formatCurrency(parseFloat(invoice.amountPaid))} was received on this void invoice. It stays void, so the
          family needs a refund.
        </Alert>
      )}

      {/* #376: classes this invoice billed nothing for, or band weeks left off (admin only) */}
      <InvoiceNotBilledNotices invoice={invoice} />

      <Grid container spacing={3}>
        {/* Invoice Summary */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 3,
                }}
              >
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {invoice.Family.name}
                  </Typography>
                  {invoice.Family.Parent.map((parent, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      color="text.secondary"
                    >
                      {parent.contact1FirstName} {parent.contact1LastName} - {parent.contact1Email}
                    </Typography>
                  ))}
                </Box>
                <Chip
                  label={invoiceStatusLabel(invoice.status)}
                  sx={{
                    backgroundColor: STATUS_COLORS[invoice.status].bg,
                    color: STATUS_COLORS[invoice.status].text,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: 1,
                  }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Invoice Info */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Invoice Number
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {invoice.invoiceNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Due Date
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color:
                        invoice.status !== 'PAID' &&
                        new Date(invoice.dueDate) < new Date()
                          ? 'error.main'
                          : 'text.primary',
                    }}
                  >
                    {formatUTCDate(invoice.dueDate, { year: 'numeric', month: 'long', day: 'numeric' })}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(invoice.createdAt)}
                  </Typography>
                </Grid>
                {invoice.sentAt && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Email Sent
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatDate(invoice.sentAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Intl.DateTimeFormat('en-AU', { hour: '2-digit', minute: '2-digit' }).format(new Date(invoice.sentAt))}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {invoice.description && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">{invoice.description}</Typography>
                </Box>
              )}

              {/* Line Items Table */}
              <Typography variant="h6" gutterBottom>
                Line Items
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#FCF6E6' }}>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoice.InvoiceItem.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell sx={{ whiteSpace: 'pre-line' }}>{displayLineDescription(item.description)}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(item.unitPrice)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500 }}>
                          {formatCurrency(item.total)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Discounts Section */}
              {invoice.status === 'DRAFT' && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Discounts
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {discountDetails && discountDetails.approvals && discountDetails.approvals.length > 0 && (
                        <Button
                          size="small"
                          startIcon={<RefreshIcon />}
                          onClick={handleRecalculate}
                          disabled={recalculateMutation.isPending}
                        >
                          Recalculate
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DiscountIcon />}
                        onClick={() => setDiscountModalOpen(true)}
                      >
                        Apply Discounts
                      </Button>
                    </Box>
                  </Box>

                  {discountDetails && discountDetails.approvals && discountDetails.approvals.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#96DAC9' }}>
                            <TableCell>Discount</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="center">Approved By</TableCell>
                            <TableCell align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {discountDetails.approvals.map((approval) => (
                            <TableRow key={approval.discountId}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {approval.discountName}
                                  {/* #625 D9: what it covers (nothing said when it covers everything). */}
                                  {appliedCoverageSuffix(approval)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 500, color: 'warning.main' }}>
                                -{formatCurrency(approval.amount)}
                              </TableCell>
                              <TableCell align="center">
                                {approval.approvedBy ? (
                                  <Typography variant="caption">
                                    {approval.approvedBy.firstName} {approval.approvedBy.lastName}
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    System
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setDiscountToRemove(approval.discountId);
                                    setRemoveDiscountDialogOpen(true);
                                  }}
                                  title="Remove discount"
                                >
                                  <RemoveCircleOutlineIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Total Discounts
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: 'warning.main' }}>
                              -{formatCurrency(
                                discountDetails.approvals.reduce(
                                  (sum, a) => sum + parseFloat(String(a.amount)),
                                  0
                                )
                              )}
                            </TableCell>
                            <TableCell colSpan={2} />
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      No discounts applied to this invoice. Click "Apply Discounts" to add discounts.
                    </Alert>
                  )}
                </Box>
              )}

              {/* Discount Audit Trail (Collapsible) */}
              {discountDetails && discountDetails.approvals && discountDetails.approvals.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      mb: 1,
                    }}
                    onClick={() => setAuditTrailExpanded(!auditTrailExpanded)}
                  >
                    <IconButton
                      size="small"
                      sx={{
                        transform: auditTrailExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    >
                      <ExpandMoreIcon />
                    </IconButton>
                    <Typography variant="h6">
                      Discount Audit Trail
                    </Typography>
                  </Box>
                  <Collapse in={auditTrailExpanded}>
                    <DiscountAuditTrail invoiceId={invoice.id} />
                  </Collapse>
                </Box>
              )}

              {/* Amendment History (2026-09-26) */}
              {(invoice.amendedCount ?? 0) > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1 }}
                    onClick={() => setAmendHistoryExpanded(!amendHistoryExpanded)}
                  >
                    <IconButton
                      size="small"
                      aria-label="Toggle amendment history"
                      sx={{
                        transform: amendHistoryExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    >
                      <ExpandMoreIcon />
                    </IconButton>
                    <Typography variant="h6">Amendment History</Typography>
                    <Chip size="small" label={invoice.amendedCount} sx={{ ml: 1 }} />
                  </Box>
                  <Collapse in={amendHistoryExpanded}>
                    <InvoiceAmendmentHistory
                      invoiceId={invoice.id}
                      timeZone={timeZone}
                      canResend={can('operations_invoices', 'write')}
                      invoiceStatus={invoice.status}
                    />
                  </Collapse>
                </Box>
              )}

              {/* Payment Reminders (2026-09-29). ⚠️ OUTSIDE the amendedCount gate above: every sent
                  invoice has a reminder history, amended or not. A paid invoice still shows the
                  reminders that went out; the button's own preview says why it is disabled. */}
              {invoice.status !== 'DRAFT' && (
                <Box sx={{ mt: 3 }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1 }}
                    onClick={() => setReminderHistoryExpanded(!reminderHistoryExpanded)}
                  >
                    <IconButton
                      size="small"
                      aria-label="Toggle payment reminders"
                      sx={{
                        transform: reminderHistoryExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    >
                      <ExpandMoreIcon />
                    </IconButton>
                    <Typography variant="h6">Payment Reminders</Typography>
                  </Box>
                  <Collapse in={reminderHistoryExpanded}>
                    {/* #372: the per-invoice stop for reminders AND late fees. */}
                    <RemindersHoldControl invoice={invoice} timeZone={timeZone} canChange={canChangeLateFees} />
                    <InvoiceReminderHistory
                      invoiceId={invoice.id}
                      timeZone={timeZone}
                      canSend={can('operations_invoices', 'write')}
                    />
                  </Collapse>
                </Box>
              )}

              {/* #372: every late fee on this invoice, waived ones struck through. */}
              <InvoiceLateFeesSection invoice={invoice} timeZone={timeZone} canWaive={canChangeLateFees} />
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Summary */}
        <Grid item xs={12} md={4}>
          {invoice.status === 'DRAFT' && heldCredit > 0 && (
            <Alert severity="info" sx={{ mb: 2 }} data-testid="draft-credit-note">
              This family has {formatCurrency(heldCredit)} account credit. It comes off their oldest open invoice
              when this invoice is sent.
            </Alert>
          )}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Summary
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Subtotal</Typography>
                  <Typography>{formatCurrency(invoice.subtotal)}</Typography>
                </Box>
                {discountDetails && discountDetails.approvals && discountDetails.approvals.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="warning.main">Discounts</Typography>
                    <Typography color="warning.main" sx={{ fontWeight: 500 }}>
                      -{formatCurrency(
                        discountDetails.approvals.reduce(
                          (sum, a) => sum + parseFloat(String(a.amount)),
                          0
                        )
                      )}
                    </Typography>
                  </Box>
                )}
                {/* #372: a taxable fee is in the tax base (before Tax); a plain one after. The rows
                    come from the server's one resolver, so this page, the PDF and the emails agree. */}
                {(invoice.lateFeeRows ?? []).filter((row) => row.beforeTax).map((row) => (
                  <Box key={`b-${row.label}`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">{row.label}</Typography>
                    <Typography>{formatCurrency(row.amount)}</Typography>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Tax</Typography>
                  <Typography>{formatCurrency(invoice.tax)}</Typography>
                </Box>
                {(invoice.lateFeeRows ?? []).filter((row) => !row.beforeTax).map((row) => (
                  <Box key={`a-${row.label}`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">{row.label}</Typography>
                    <Typography>{formatCurrency(row.amount)}</Typography>
                  </Box>
                ))}
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontWeight: 600 }}>Total</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
                    {formatCurrency(invoice.total)}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="success.main">Amount Paid</Typography>
                  <Typography color="success.main" sx={{ fontWeight: 500 }}>
                    {formatCurrency(invoice.amountPaid)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    p: 1.5,
                    bgcolor: balance.owing
                      ? 'warning.light'
                      : invoice.status === 'CANCELLED'
                        ? 'grey.200'
                        : 'success.light',
                    borderRadius: 1,
                    mt: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>{balance.label}</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
                    {formatCurrency(balance.amount)}
                  </Typography>
                </Box>
                {/* #642 Phase 2 Request 6: what the family holds (the panel on the family page has the detail). */}
                {familyCredit && Number(familyCredit.balance) > 0 && invoice.status !== 'CANCELLED' && (
                  <Box data-testid="summary-credit-on-account" sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Box>
                      <Typography color="text.secondary">Credit on account</Typography>
                      {Number(familyCredit.refundDue) > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {formatCurrency(familyCredit.refundDue)} being refunded
                        </Typography>
                      )}
                    </Box>
                    <Typography sx={{ fontWeight: 500 }}>{formatCurrency(familyCredit.balance)}</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Payment History */}
          {invoice.Payment.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment History
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {invoice.Payment.map((payment) => (
                    <Box
                      key={payment.id}
                      data-testid={payment.voidedAt ? 'payment-row-voided' : 'payment-row'}
                      sx={{
                        p: 1.5,
                        bgcolor: '#FCF6E6',
                        borderRadius: 1,
                        opacity: payment.voidedAt ? 0.7 : 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 1,
                        }}
                      >
                        {/* `break-word`, never `anywhere`: `anywhere` lets the column shrink to one letter
                            wide, so "$200.00" broke as "$20 / 0.0 / 0" (Andrew 30/10/2026). */}
                        <Box sx={{ flex: 1, minWidth: 0, overflowWrap: 'break-word' }}>
                          {/* The amount alone on its line; the fee and the credit part underneath. */}
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              textDecoration: payment.voidedAt ? 'line-through' : 'none',
                            }}
                          >
                            {formatCurrency(payment.amount)}
                          </Typography>
                          {paymentDetailText(payment.processingFee, paymentToCredit(payment)) && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', textDecoration: payment.voidedAt ? 'line-through' : 'none' }}
                            >
                              {paymentDetailText(payment.processingFee, paymentToCredit(payment))}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {/* An account-credit row says so once, in its chip. */}
                            {payment.method !== 'ACCOUNT_CREDIT' && <>{PAYMENT_METHOD_LABELS[payment.method]} </>}
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{
                                bgcolor: payment.stripePaymentId
                                  ? 'primary.light'
                                  : payment.method === 'ACCOUNT_CREDIT'
                                    ? 'secondary.light'
                                    : 'grey.300',
                                color: payment.stripePaymentId ? 'primary.dark' : 'text.secondary',
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 0.5,
                                fontWeight: 500,
                              }}
                            >
                              {payment.stripePaymentId ? 'Stripe' : payment.method === 'ACCOUNT_CREDIT' ? 'Account credit' : 'Admin'}
                            </Typography>
                            {payment.reference && ` - ${payment.reference}`}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                            Paid {formatPaymentDay(payment.paidAt, timeZone)}
                          </Typography>
                          {canTakeOff(payment) && canVoidPayments && (
                            <Box sx={{ mt: 0.5 }}>
                              {/* The explanation is a tooltip (and the dialog says it in full): as a line
                                  here it made this column too wide for the amount (Andrew 30/10/2026). */}
                              <Tooltip describeChild title="The amount goes back to the family's credit">
                                <Button
                                  size="small"
                                  startIcon={<UndoIcon fontSize="small" />}
                                  onClick={() => {
                                    setPaymentToVoid(payment);
                                    setVoidPaymentOpen(true);
                                  }}
                                >
                                  Take off
                                </Button>
                              </Tooltip>
                            </Box>
                          )}
                          {isAmendable(payment) && (canEditPayments || canVoidPayments) && (
                            <Box sx={{ mt: 0.5 }}>
                              {canEditPayments && (
                                <IconButton
                                  size="small"
                                  aria-label="Edit payment"
                                  // 44px touch target on phones
                                  sx={{ p: { xs: 1.25, sm: 0.5 } }}
                                  onClick={() => {
                                    setPaymentToEdit(payment);
                                    setEditPaymentOpen(true);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              )}
                              {canVoidPayments && (
                                <IconButton
                                  size="small"
                                  aria-label="Void payment"
                                  sx={{ p: { xs: 1.25, sm: 0.5 } }}
                                  onClick={() => {
                                    setPaymentToVoid(payment);
                                    setVoidPaymentOpen(true);
                                  }}
                                >
                                  <BlockIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          )}
                          {/* #642 Phase 2 Request 7: the owner flags a live payment to check against the bank. */}
                          {isOwner && !payment.voidedAt && payment.method !== 'ACCOUNT_CREDIT' && !paymentNeedsChecking(payment) && (
                            <Box sx={{ mt: 0.5 }}>
                              <Tooltip describeChild title="Mark as needs checking (against the bank)">
                                <IconButton
                                  size="small"
                                  aria-label="Mark as needs checking"
                                  sx={{ p: { xs: 1.25, sm: 0.5 } }}
                                  onClick={() => openCheck(payment, 'set')}
                                >
                                  <OutlinedFlagIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      </Box>
                      {/* Full width, under both columns: beside the Take off button or the icons it
                          wrapped "10:11 / am" (Andrew 30/10/2026). */}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Recorded {formatRecordedAt(payment.createdAt, timeZone)}
                      </Typography>
                      {/* #642 Phase 2 Request 7: the owner's flag, full width. The OWNER's only (the server
                          sends staff none of it; Andrew 30/10/2026). */}
                      {isOwner && paymentNeedsChecking(payment) && payment.needsCheckingReason && (
                        <Box data-testid="payment-needs-checking" sx={{ mt: 0.5 }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ bgcolor: 'warning.light', px: 0.75, py: 0.25, borderRadius: 0.5, fontWeight: 600 }}
                            >
                              Needs checking: {PAYMENT_CHECK_REASON_LABELS[payment.needsCheckingReason]}
                            </Typography>
                            {isOwner && (
                              <Button size="small" onClick={() => openCheck(payment, 'resolve')}>
                                Resolve
                              </Button>
                            )}
                          </Box>
                          {(payment.needsCheckingNote || payment.needsCheckingByName) && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {payment.needsCheckingNote}
                              {payment.needsCheckingByName &&
                                `${payment.needsCheckingNote ? ' · ' : ''}Flagged${
                                  payment.needsCheckingAt ? ` ${formatPaymentDay(payment.needsCheckingAt, timeZone)}` : ''
                                } by ${payment.needsCheckingByName}`}
                            </Typography>
                          )}
                        </Box>
                      )}
                      {isOwner && !paymentNeedsChecking(payment) && payment.checkResolvedAt && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Checked {formatPaymentDay(payment.checkResolvedAt, timeZone)}: {payment.checkResolutionNote}
                          {payment.checkResolvedByName ? ` (${payment.checkResolvedByName})` : ''}
                        </Typography>
                      )}
                      {payment.notes && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          {payment.notes}
                        </Typography>
                      )}
                      {payment.voidedAt && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                          Voided {formatPaymentDay(payment.voidedAt, timeZone)}: {payment.voidReason}
                          {payment.voidedByName ? ` (${payment.voidedByName})` : ''}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Dialogs */}
      <RecordPaymentDialog
        open={recordPaymentOpen}
        onClose={() => setRecordPaymentOpen(false)}
        invoice={invoice}
      />

      <RecordPaymentDialog
        open={editPaymentOpen}
        onClose={() => setEditPaymentOpen(false)}
        invoice={invoice}
        payment={paymentToEdit}
      />

      <VoidPaymentDialog
        open={voidPaymentOpen}
        onClose={() => setVoidPaymentOpen(false)}
        invoiceId={invoice.id}
        payment={paymentToVoid}
      />

      {isOwner && (
        <NeedsCheckingDialog
          open={checkOpen}
          onClose={() => setCheckOpen(false)}
          mode={checkMode}
          payment={paymentToCheck}
        />
      )}

      {/* DRAFT edit — same dialog and gate as the invoice list's Edit action. The invoice
          is passed only while open so the form re-initialises from fresh data each time. */}
      <EditInvoiceDialog
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setAmendLink(null);
          setAmendBanner(null);
        }}
        invoice={editOpen ? invoice : null}
        // Set only when opened from "An invoice may need updating" (a draft's deep link).
        amendContext={editOpen ? amendBanner : null}
        // A draft edit can add/remove discounts (2026-10-02). Read is separate: the
        // saved-discount list needs it, and write does not imply it.
        canApplyDiscount={can('operations_pricing', 'write')}
        canRemoveDiscount={can('operations_pricing', 'delete')}
        canReadPricing={can('operations_pricing', 'read')}
      />

      <EditInvoiceDialog
        open={amendOpen}
        onClose={() => {
          setAmendOpen(false);
          // A fresh snapshot (and a fresh form) on the next open.
          setAmendTarget(null);
          setAmendLink(null);
          setAmendBanner(null);
        }}
        invoice={amendTarget}
        mode="amend"
        amendContext={amendBanner}
        schoolTimeZone={timeZone}
        canApplyDiscount={can('operations_pricing', 'write')}
        canRemoveDiscount={can('operations_pricing', 'delete')}
        // #625 (/qa phase 5 M2): the school default coverage and the category list need it.
        canReadPricing={can('operations_pricing', 'read')}
      />

      {amendLink?.lessonId && <AmendContextLoader link={amendLink} onContext={setAmendBanner} />}

      <AddProductToInvoiceItemDialog
        open={addProductDialogOpen}
        onClose={() => setAddProductDialogOpen(false)}
        invoiceId={invoice.id}
      />

      <ConfirmDialog
        open={sendDialogOpen}
        title="Send Invoice"
        message={`Are you sure you want to send this invoice to ${invoice.Family.name}? They will receive an email notification.`}
        confirmLabel="Send"
        onConfirm={handleSend}
        onCancel={() => setSendDialogOpen(false)}
        loading={sendMutation.isPending}
      />

      <VoidInvoiceDialog
        open={cancelDialogOpen}
        invoiceNumber={invoice.invoiceNumber}
        loading={cancelMutation.isPending}
        onConfirm={handleVoid}
        onClose={() => setCancelDialogOpen(false)}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Invoice"
        message="Are you sure you want to delete this draft invoice? This action cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
        loading={deleteMutation.isPending}
      />

      {/* Discount Approval Modal */}
      <InvoiceDiscountApprovalModal
        open={discountModalOpen}
        onClose={() => setDiscountModalOpen(false)}
        invoiceId={invoice.id}
        invoice={invoice}
      />

      {/* Remove Discount Dialog */}
      <Dialog
        open={removeDiscountDialogOpen}
        onClose={() => {
          setRemoveDiscountDialogOpen(false);
          setDiscountToRemove(null);
          setRemovalReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remove Discount</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for removing this discount. This will be recorded in the audit trail.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Reason for removal"
            value={removalReason}
            onChange={(e) => setRemovalReason(e.target.value)}
            placeholder="e.g., Applied incorrect discount, customer no longer eligible"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRemoveDiscountDialogOpen(false);
              setDiscountToRemove(null);
              setRemovalReason('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemoveDiscount}
            color="error"
            variant="contained"
            disabled={!removalReason.trim() || removeDiscountMutation.isPending}
          >
            Remove Discount
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
